import * as React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { ThemeProvider } from './contexts/ThemeContext';
import { ChartCacheProvider } from './contexts/ChartCacheContext';

import './App.css';
import LoginPage from './pages/LoginPage';
import Sidebar from './components/Sidebar/Sidebar';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import PoweredBy from './components/PoweredBy/PoweredBy';
import { AcessoProvider, useAcesso } from './contexts/AcessoContext';
import {
    EXIGE, INICIO_ACESSOS, INICIO_SIMULADOR, Inicio, InicioDe, RotaProtegida,
} from './components/Acessos/RotaProtegida';
import TelaBloqueio from './components/Acessos/TelaBloqueio';
import { renovarAcessoApi } from './utils/apiClient';

// Lazy load pages for code-splitting
const IndicadoresPage = React.lazy(() => import('./pages/IndicadoresPage'));
const AnalisesPage = React.lazy(() => import('./pages/AnalisesPage'));
const SimuladorAlavancagemPage = React.lazy(() => import('./pages/SimuladorAlavancagemPage'));
const FluxoCaixaPage = React.lazy(() => import('./pages/FluxoCaixaPage'));
const OrcamentoPage = React.lazy(() => import('./pages/OrcamentoPage'));
const MetodologiaPage = React.lazy(() => import('./pages/MetodologiaPage'));
const AcessosPage = React.lazy(() => import('./pages/AcessosPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Loading fallback component
const PageLoader = () => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        color: '#6B7280',
        fontSize: '1rem',
    }}>
        <div style={{ textAlign: 'center' }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #E5E7EB',
                borderTopColor: '#3B82F6',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 1rem',
            }} />
            Carregando...
        </div>
    </div>
);

/**
 * Rotas da aplicação, na navegação do protótipo ANCORA_software_refatorado.html
 * (spec 20261019-refatoracao-navegacao): o Simulador é o início, e o passo e a aba
 * ficam na URL para os links "por quê?". Separadas do `MainContent` para serem
 * testáveis sem o MSAL.
 *
 * Cada página exige a permissão da sua tela (spec 20261019-gestao-de-acessos, RF10); o início
 * é a primeira página que a pessoa pode ver. Fora de `aplicar`, tudo é permitido.
 */
export function RotasApp() {
    return (
        <Routes>
            <Route path="/" element={<Inicio />} />
            <Route path="/simulador" element={<InicioDe opcoes={INICIO_SIMULADOR} />} />
            <Route path="/simulador/:passo" element={
                <RotaProtegida exige={EXIGE.simuladorPasso}><SimuladorAlavancagemPage /></RotaProtegida>} />
            <Route path="/analises" element={<Navigate to="/analises/liquidez" replace />} />
            <Route path="/analises/:aba" element={
                <RotaProtegida exige={EXIGE.analisesAba}><AnalisesPage /></RotaProtegida>} />
            <Route path="/mercado" element={<RotaProtegida exige={EXIGE.mercado}><IndicadoresPage /></RotaProtegida>} />
            <Route path="/fluxo-caixa" element={
                <RotaProtegida exige={EXIGE.fluxoCaixa}><FluxoCaixaPage /></RotaProtegida>} />
            <Route path="/orcamento" element={
                <RotaProtegida exige={EXIGE.orcamento}><OrcamentoPage /></RotaProtegida>} />
            <Route path="/metodologia" element={
                <RotaProtegida exige={EXIGE.metodologia}><MetodologiaPage /></RotaProtegida>} />
            <Route path="/acessos" element={<InicioDe opcoes={INICIO_ACESSOS} />} />
            <Route path="/acessos/:aba" element={<RotaProtegida exige={EXIGE.acessosAba}><AcessosPage /></RotaProtegida>} />

            {/* Rotas antigas */}
            <Route path="/simulador-alavancagem" element={<Navigate to="/simulador/decisao" replace />} />
            <Route path="/alavancagem" element={<Navigate to="/analises/liquidez" replace />} />
            {/* Data Driven: o Monte Carlo foi para o fim do Crescimento e os históricos para
                Análises › Resultado; Markowitz e o crescimento legado saíram (Metodologia) */}
            <Route path="/simulations" element={<Navigate to="/simulador/crescimento" replace />} />

            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
}

function MainContent() {
    const { instance } = useMsal();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

    const handleLogout = () => {
        instance.logoutPopup({
            postLogoutRedirectUri: "/",
            mainWindowRedirectUri: "/"
        });
    };

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    return (
        <AcessoProvider>
            <Casca
                onLogout={handleLogout}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
            />
        </AcessoProvider>
    );
}

/**
 * A aplicação depois do login. Espera o `/api/acessos/me/` (uma chamada leve) para não mostrar
 * a página e depois trocá-la pela tela de bloqueio; em `aplicar`, quem está Aguardando,
 * Inativo ou sem token vê a tela de bloqueio no lugar do sistema inteiro.
 */
function Casca({ onLogout, isSidebarCollapsed, toggleSidebar }) {
    const { carregando, bloqueio, contato } = useAcesso();

    if (carregando) return <PageLoader />;
    if (bloqueio) {
        return (
            <TelaBloqueio tipo={bloqueio} contato={contato} onSair={onLogout} onRenovar={renovarAcessoApi}
                paginaInteira />
        );
    }

    return (
        <div className={`App ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <Sidebar onLogout={onLogout} isCollapsed={isSidebarCollapsed} toggleSidebar={toggleSidebar} />
            <main className="content">
                <ErrorBoundary message="Erro ao carregar a página. Tente recarregar.">
                    <React.Suspense fallback={<PageLoader />}>
                        <RotasApp />
                    </React.Suspense>
                </ErrorBoundary>
                {/* Assinatura no fim do conteúdo — como o <main> é o container de
                    scroll de todas as rotas, vale para todas as páginas e abas. */}
                <PoweredBy />
            </main>
        </div>
    );
}


function App() {
  return (
    <ThemeProvider>
      <ChartCacheProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthenticatedTemplate>
            <MainContent />
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <LoginPage />
          </UnauthenticatedTemplate>
        </Router>
      </ChartCacheProvider>
    </ThemeProvider>
  );
}

export default App;
